import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api, fmtErr } from "@/lib/api";
import { toast } from "sonner";
import { Scale, Plus, Trash2, Lock, LogOut } from "lucide-react";

const empty = { state: "California", category: "", rule: "", source: "", source_url: "", effective_date: "", last_reviewed: "", confidence: "high", doc_types: ["prenuptial", "postnuptial"] };
const inp = "w-full bg-[#F8F9FA] border border-[#E2E8F0] rounded-lg px-3 py-2 text-sm";
const isTestAccount = (u) => /(@example\.com|test|qa|demo|trial_|owner_|tester_|^t_)/i.test(u.email || "");
const subBadge = (s) => s === "active" ? "bg-green-100 text-green-800" : s === "trialing" ? "bg-blue-100 text-blue-800" : s === "past_due" ? "bg-amber-100 text-amber-800" : "bg-gray-100 text-gray-600";
const STORAGE_KEY = "nupdocs_admin_passcode";

export default function Admin() {
  const [code, setCode] = useState("");
  const [unlocked, setUnlocked] = useState(false);
  const [checking, setChecking] = useState(true);
  const [pw, setPw] = useState("");
  const [authErr, setAuthErr] = useState("");
  const [tab, setTab] = useState("Users");
  const [stats, setStats] = useState(null);
  const [rules, setRules] = useState([]);
  const [users, setUsers] = useState([]);
  const [payments, setPayments] = useState(null);
  const [pricing, setPricing] = useState(null);
  const [ccosts, setCcosts] = useState([]);
  const [form, setForm] = useState(empty);
  const [uq, setUq] = useState("");
  const [hideTest, setHideTest] = useState(true);

  const loadAll = useCallback(async (c) => {
    const cfg = { headers: { "X-Admin-Passcode": c } };
    const [st, us, pay] = await Promise.all([api.get("/admin/stats", cfg), api.get("/admin/users", cfg), api.get("/admin/payments", cfg)]);
    setStats(st.data); setUsers(us.data); setPayments(pay.data);
    const [rl, pr, cc] = await Promise.all([api.get("/rules"), api.get("/pricing"), api.get("/credit-costs")]);
    setRules(rl.data); setPricing(pr.data); setCcosts(cc.data.actions);
  }, []);

  useEffect(() => {
    document.title = "NuPDocs Admin";
    const meta = document.createElement("meta"); meta.name = "robots"; meta.content = "noindex,nofollow"; document.head.appendChild(meta);
    const saved = sessionStorage.getItem(STORAGE_KEY);
    if (!saved) { setChecking(false); return () => { document.head.removeChild(meta); }; }
    loadAll(saved).then(() => { setCode(saved); setUnlocked(true); }).catch(() => sessionStorage.removeItem(STORAGE_KEY)).finally(() => setChecking(false));
    return () => { document.head.removeChild(meta); };
  }, [loadAll]);

  const unlock = async () => {
    if (!pw.trim()) return;
    setAuthErr("");
    try {
      await loadAll(pw);
      sessionStorage.setItem(STORAGE_KEY, pw);
      setCode(pw); setUnlocked(true); setPw("");
    } catch (e) {
      setAuthErr(e.response?.status === 401 ? "Incorrect admin passcode." : fmtErr(e.response?.data?.detail));
    }
  };
  const lock = () => { sessionStorage.removeItem(STORAGE_KEY); setUnlocked(false); setCode(""); };

  const cfg = { headers: { "X-Admin-Passcode": code } };
  const loadRules = () => api.get("/rules").then((r) => setRules(r.data));
  const saveRule = async () => {
    try { await api.post("/admin/rules", form, cfg); toast.success("Rule added"); setForm(empty); loadRules(); }
    catch (e) { toast.error(fmtErr(e.response?.data?.detail)); }
  };
  const delRule = async (id) => { try { await api.delete(`/admin/rules/${id}`, cfg); toast.success("Deleted"); loadRules(); } catch (e) { toast.error(fmtErr(e.response?.data?.detail)); } };
  const savePricing = async () => {
    try { await api.put("/admin/pricing", { subscription_price: +pricing.subscription_price, credit_packs: pricing.credit_packs }, cfg); toast.success("Pricing updated"); }
    catch (e) { toast.error(fmtErr(e.response?.data?.detail)); }
  };
  const saveCcosts = async () => {
    try { await api.put("/admin/credit-costs", { actions: ccosts }, cfg); toast.success("Credit costs updated"); }
    catch (e) { toast.error(fmtErr(e.response?.data?.detail)); }
  };

  if (checking) return <div className="min-h-screen flex items-center justify-center text-[#5C6B73]">Loading…</div>;

  if (!unlocked) return (
    <div className="min-h-screen marketing-bg flex items-center justify-center px-5">
      <div className="bg-white border border-[#E2E8F0] rounded-2xl p-8 w-full max-w-sm text-center shadow-sm">
        <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-[#0B132B] mb-4"><Lock className="w-5 h-5 text-white" /></div>
        <h1 className="font-display text-2xl font-bold text-[#0B132B]">NuPDocs Admin</h1>
        <p className="text-sm text-[#5C6B73] mt-1 mb-5">Enter the admin passcode to view users and manage the platform.</p>
        <input type="password" autoFocus className={inp + " text-center"} placeholder="Admin passcode" value={pw} onChange={(e) => setPw(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") unlock(); }} data-testid="admin-passcode-input" />
        {authErr && <div className="text-sm text-red-600 mt-3" data-testid="admin-passcode-error">{authErr}</div>}
        <button onClick={unlock} data-testid="admin-unlock-btn" className="mt-4 w-full bg-[#0B132B] text-white px-6 py-2.5 rounded-full font-medium hover:bg-[#1C2541] transition-colors">Unlock</button>
        <Link to="/" className="block mt-4 text-xs text-[#4A6FA5]">Back to site</Link>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-white">
      <header className="border-b border-[#E2E8F0]">
        <div className="max-w-6xl mx-auto px-5 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2"><Scale className="w-6 h-6 text-[#0B132B]" /><span className="font-display text-xl font-semibold text-[#0B132B]">NuPDocs Admin</span></Link>
          <button onClick={lock} data-testid="admin-lock-btn" className="flex items-center gap-2 text-sm text-[#5C6B73] hover:text-[#0B132B]"><LogOut className="w-4 h-4" /> Lock</button>
        </div>
      </header>
      <div className="max-w-6xl mx-auto px-5 py-8">
        {stats && (
          <div className="grid grid-cols-4 gap-4 mb-8">
            {[["Users", stats.users], ["Agreements", stats.matters], ["State rules", stats.rules], ["Active subs", stats.active_subs]].map(([l, v]) => (
              <div key={l} className="border border-[#E2E8F0] rounded-xl p-4"><div className="font-display text-2xl font-bold text-[#0B132B]">{v}</div><div className="text-xs text-[#5C6B73]">{l}</div></div>
            ))}
          </div>
        )}
        <div className="flex gap-2 border-b border-[#E2E8F0] mb-6">
          {["Users", "Payments", "Rules", "Pricing"].map((t) => <button key={t} onClick={() => setTab(t)} data-testid={`admin-tab-${t}`} className={`px-4 py-2 text-sm border-b-2 -mb-px ${tab === t ? "border-[#0B132B] text-[#0B132B] font-medium" : "border-transparent text-[#5C6B73]"}`}>{t}</button>)}
        </div>

        {tab === "Users" && (() => {
          const realCount = users.filter((u) => !isTestAccount(u)).length;
          const q = uq.trim().toLowerCase();
          const filtered = users.filter((u) => (!hideTest || !isTestAccount(u)) && (!q || (u.email || "").toLowerCase().includes(q) || (u.name || "").toLowerCase().includes(q)));
          return (
            <div>
              <div className="flex flex-wrap items-center gap-x-4 gap-y-3 mb-5">
                <div className="text-sm text-[#0B132B]" data-testid="users-summary">
                  <span className="font-display text-xl font-bold">{users.length}</span> total
                  <span className="mx-2 text-[#E2E8F0]">|</span>
                  <span className="font-semibold text-green-700">{realCount}</span> real-looking
                  <span className="mx-2 text-[#E2E8F0]">|</span>
                  <span className="text-[#5C6B73]">{users.length - realCount} test/QA</span>
                </div>
                <input data-testid="user-search" className={inp + " max-w-xs"} placeholder="Search name or email" value={uq} onChange={(e) => setUq(e.target.value)} />
                <label className="flex items-center gap-2 text-xs text-[#5C6B73] cursor-pointer"><input type="checkbox" data-testid="hide-test-toggle" checked={hideTest} onChange={(e) => setHideTest(e.target.checked)} /> Hide test accounts</label>
              </div>
              <div className="space-y-2" data-testid="admin-users-list">
                {filtered.length === 0 && <div className="text-sm text-[#5C6B73] border border-dashed border-[#E2E8F0] rounded-lg p-4">No users match.</div>}
                {filtered.map((u) => (
                  <div key={u.id} className="flex flex-wrap justify-between items-center gap-2 border border-[#E2E8F0] rounded-lg p-4 text-sm" data-testid={`admin-user-${u.id}`}>
                    <div className="min-w-0">
                      <div className="font-medium text-[#0B132B] truncate">{u.name || "—"}</div>
                      <div className="text-[#5C6B73] truncate">{u.email}</div>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap justify-end">
                      {u.role === "admin" && <span className="text-[10px] font-semibold uppercase bg-[#0B132B] text-white px-2 py-0.5 rounded-full">admin</span>}
                      <span className={`text-[10px] font-semibold uppercase px-2 py-0.5 rounded-full ${subBadge(u.subscription_status)}`}>{u.subscription_status || "none"}</span>
                      <span className="text-[#5C6B73]">{u.credits || 0} cr</span>
                      <span className="text-xs text-[#5C6B73] w-24 text-right">{u.created_at ? new Date(u.created_at).toLocaleDateString() : ""}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })()}

        {tab === "Payments" && payments && (
          <div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-5">
              {[["Total revenue", `$${payments.total_revenue.toFixed(2)}`], ["Subscriptions", `$${payments.subscription_revenue.toFixed(2)}`], ["Credit packs", `$${payments.credits_revenue.toFixed(2)}`], ["Paid orders", payments.paid_count]].map(([l, v]) => (
                <div key={l} className="border border-[#E2E8F0] rounded-xl p-4" data-testid={`revenue-${l}`}><div className="font-display text-2xl font-bold text-[#0B132B]">{v}</div><div className="text-xs text-[#5C6B73]">{l}</div></div>
              ))}
            </div>
            <p className="text-xs text-[#5C6B73] mb-4">Revenue is estimated from paid checkout sessions using current pricing. Recurring subscription renewals after the first payment are managed by Stripe and may not all appear here — see your Stripe dashboard for authoritative figures.</p>
            <div className="space-y-2" data-testid="admin-payments-list">
              {payments.transactions.length === 0 && <div className="text-sm text-[#5C6B73] border border-dashed border-[#E2E8F0] rounded-lg p-4">No paid transactions yet.</div>}
              {payments.transactions.map((t) => (
                <div key={t.session_id} className="flex flex-wrap justify-between items-center gap-2 border border-[#E2E8F0] rounded-lg p-4 text-sm" data-testid="admin-payment-row">
                  <div className="min-w-0"><div className="font-medium text-[#0B132B] truncate">{t.email}</div><div className="text-[#5C6B73] truncate">{t.kind === "credits" ? `${t.credits} credits` : "Subscription"}</div></div>
                  <div className="flex items-center gap-3"><span className="font-semibold text-[#0B132B]">${t.amount.toFixed(2)}</span><span className="text-xs text-[#5C6B73]">{t.date ? new Date(t.date).toLocaleDateString() : ""}</span></div>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "Rules" && (
          <div>
            <div className="border border-[#E2E8F0] rounded-xl p-5 mb-6">
              <h3 className="font-semibold text-[#0B132B] mb-3">Add / update a state rule (no redeploy needed)</h3>
              <div className="grid sm:grid-cols-2 gap-3">
                <select className={inp} data-testid="rule-state" value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })}>{["California", "Texas", "New York", "Florida", "Illinois", "Massachusetts"].map((s) => <option key={s}>{s}</option>)}</select>
                <input className={inp} data-testid="rule-category" placeholder="Category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} />
                <textarea className={inp + " sm:col-span-2"} data-testid="rule-text" placeholder="Rule text" value={form.rule} onChange={(e) => setForm({ ...form, rule: e.target.value })} />
                <input className={inp} data-testid="rule-source" placeholder="Source (statute/citation)" value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })} />
                <input className={inp} placeholder="Last reviewed (YYYY-MM-DD)" value={form.last_reviewed} onChange={(e) => setForm({ ...form, last_reviewed: e.target.value })} />
                <select className={inp} value={form.confidence} onChange={(e) => setForm({ ...form, confidence: e.target.value })}><option value="high">high</option><option value="medium">medium</option><option value="low">low</option></select>
              </div>
              <button onClick={saveRule} data-testid="save-rule-btn" className="mt-3 inline-flex items-center gap-2 bg-[#0B132B] text-white px-4 py-2 rounded-full text-sm"><Plus className="w-4 h-4" />Add rule</button>
            </div>
            <div className="space-y-2">
              {rules.map((r) => (
                <div key={r.id} className="flex items-start justify-between border border-[#E2E8F0] rounded-lg p-4" data-testid={`admin-rule-${r.id}`}>
                  <div><div className="text-sm font-medium text-[#0B132B]">{r.state} · {r.category} <span className="text-xs text-[#5C6B73]">v{r.version}</span></div><div className="text-xs text-[#5C6B73] mt-1">{r.rule}</div><div className="text-xs text-[#4A6FA5] mt-1">{r.source} · {r.confidence}</div></div>
                  <button onClick={() => delRule(r.id)} className="text-red-500"><Trash2 className="w-4 h-4" /></button>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "Pricing" && pricing && (
          <div className="border border-[#E2E8F0] rounded-xl p-5 max-w-lg">
            <label className="text-xs text-[#5C6B73]">Subscription price / month</label>
            <input className={inp + " mb-4"} data-testid="pricing-sub" value={pricing.subscription_price} onChange={(e) => setPricing({ ...pricing, subscription_price: e.target.value })} />
            <div className="text-xs text-[#5C6B73] mb-2">Credit packs</div>
            {pricing.credit_packs.map((c, i) => (
              <div key={c.id} className="flex gap-2 mb-2">
                <input className={inp} value={c.credits} onChange={(e) => { const p = { ...pricing }; p.credit_packs[i].credits = +e.target.value; setPricing(p); }} />
                <input className={inp} value={c.price} onChange={(e) => { const p = { ...pricing }; p.credit_packs[i].price = +e.target.value; setPricing(p); }} />
              </div>
            ))}
            <button onClick={savePricing} data-testid="save-pricing-btn" className="mt-3 bg-[#0B132B] text-white px-4 py-2 rounded-full text-sm">Save pricing</button>
            <div className="mt-8 border-t border-[#E2E8F0] pt-4">
              <div className="text-xs text-[#5C6B73] mb-2">Credit cost per customized action (quoted to users before they spend)</div>
              {ccosts.map((a, i) => (
                <div key={a.id} className="flex gap-2 mb-2 items-center">
                  <span className="text-sm text-[#0B132B] flex-1">{a.label}</span>
                  <input className={inp + " w-24"} data-testid={`ccost-${a.id}`} value={a.credits} onChange={(e) => { const c = [...ccosts]; c[i] = { ...c[i], credits: +e.target.value }; setCcosts(c); }} />
                  <span className="text-xs text-[#5C6B73]">credits</span>
                </div>
              ))}
              <button onClick={saveCcosts} data-testid="save-ccosts-btn" className="mt-2 bg-[#0B132B] text-white px-4 py-2 rounded-full text-sm">Save credit costs</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
